import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { IState } from 'models/reducers/i-state.interface';

import { VIP_SYSTEM } from 'constants/vip-system.constant';

import EventArenaSection from './components/EventArenaSection/EventArenaSection';
import IntroSection from './components/IntroSection/IntroSection';
import MyStatisticsSection from './components/MyStatisticsSection/MyStatisticsSection';
import ParticipationCriteriaSection from './components/ParticipationCriteriaSection/ParticipationCriteriaSection';
import PrizePoolSection from './components/PrizePoolSection/PrizePoolSection';
import Page from 'components/page/Page';
import PageWrapper from 'components/pageWrapper/PageWrapper';
import Spacing from 'components/spacing/Spacing';

import { useWorldNftEvent } from './hooks/useWorldNftEvent';
import { useNftCountById } from 'hooks/queries';


import { replacePlaceholders } from './utils/replacePlaceholders';

type WorldNftEventRouteParams = {
  id: string;
};

const WorldNftEventRoute = () => {
  const { id } = useParams<WorldNftEventRouteParams>();
  const { event, loading } = useWorldNftEvent({ eventId: id || '' });

  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const worldNftCount = useNftCountById(VIP_SYSTEM.WORLD_NFT_TOKEN_ID).count;

  // Event-specific dynamic data configuration
  const eventDynamicConfig: Record<string, Record<string, (...args: any[]) => string>> = useMemo(
    () => ({
      'world-nft': {
        'world-nft-balance': (multiplier: number = 1) =>
          worldNftCount ? (worldNftCount * multiplier).toString() : '-',
      },
    }),
    [worldNftCount]
  );

  // World NFT event never shows estimated rewards section
  const showEstimatedRewards = false;

  // Inject real-time data into event statistics and estimated rewards
  const eventWithRealData = useMemo(() => {
    if (!event) return event;

    const eventConfig = id ? eventDynamicConfig[id] : undefined;
    if (!eventConfig) return event;

    // Replace placeholders in statistics values
    const updatedStatistics = event.myStatistics.statistics.map((stat) => ({
      ...stat,
      value: replacePlaceholders(stat.value, eventConfig),
    }));

    // Replace placeholders in estimated reward names
    const updatedEstimatedReward = event.myStatistics.estimatedReward.map((reward) => ({
      ...reward,
      name: replacePlaceholders(reward.name, eventConfig),
    }));

    return {
      ...event,
      myStatistics: {
        ...event.myStatistics,
        statistics: updatedStatistics,
        estimatedReward: updatedEstimatedReward,
      },
    };
  }, [event, id, worldNftCount, eventDynamicConfig]);

  return (
    <Page
      withScrollbar
      className="tw-overflow-x-hidden tw-text-center"
      pageBackground="dark"
      requireTermsOfServiceConsents
      loading={loading}
    >
      <PageWrapper homeFooter={false} landingFooter>
        {eventWithRealData && (
          <IntroSection
            name={eventWithRealData.intro.title}
            tagline={eventWithRealData.intro.subtitle}
            iconUrl={eventWithRealData.intro.heroImage}
            imageClassName={eventWithRealData.intro.heroImageClassName}
            endAt={eventWithRealData.endAt}
            stages={eventWithRealData.stages}
            eventInfo={{
              eventId: id!,
              walletAddress,
            }}
          />
        )}
        <Spacing />
        <PrizePoolSection
          prizePool={eventWithRealData?.prizePool || []}
          prizePoolHighlights={eventWithRealData?.prizePoolHighlights || []}
        />
        {eventWithRealData && (
          <MyStatisticsSection
            myStatistics={eventWithRealData.myStatistics}
            showEstimatedRewards={showEstimatedRewards}
          />
        )}
        {/* Sits directly under the numbers it qualifies, deliberately. The figures above
            are a member's WNFT holdings; what the event pays on is their EVENT QUALIFYING
            balance, which excludes anything held at the July 1 snapshot and anything
            bought on the secondary market. Someone reading a total of 35 and expecting a
            35-WNFT reward tier is the misunderstanding this line exists to prevent, so it
            belongs beside the total rather than only in the rules further down. */}
        {eventWithRealData && (
          <div className="tw-mx-auto tw-mt-[16px] tw-max-w-[960px] tw-px-[20px] tw-text-center lg:tw-px-0">
            <p className="tw-text-[14px] tw-text-white/60">
              Not all NFTs are eligible for this event —{' '}
              <a href="#participation-criteria" className="tw-text-turquoise tw-underline">
                read the participation criteria
              </a>
              .
            </p>
          </div>
        )}
        <Spacing mobileHeight={60} />
        {eventWithRealData?.secondaryLeaderboard && (
          <>
            <EventArenaSection
              leaderboard={eventWithRealData.secondaryLeaderboard}
              variant="world-nft"
            />
            <Spacing height={60} />
          </>
        )}
        {eventWithRealData && (
          <EventArenaSection
            leaderboard={eventWithRealData.leaderboard}
            variant="world-nft"
          />
        )}
        <Spacing height={60} />
        <div className="tw-bg-darkBlue">
          <Spacing />
          {eventWithRealData && <ParticipationCriteriaSection criteria={eventWithRealData.participationCriteria} />}
          <Spacing />
        </div>
      </PageWrapper>
    </Page>
  );
};

export default WorldNftEventRoute;
